import { SxProps, Theme } from '@mui/material/styles';

const timesheetBaseCellStyles: SxProps<Theme> = {
  padding: '6px 8px',
  whiteSpace: 'nowrap',
  borderBottom: '1px solid #e5e7eb',
  fontSize: '0.8125rem',
  color: '#374151',
};

const timesheetStickyColumnStyles: SxProps<Theme> = {
  position: 'sticky',
  backgroundColor: '#fff',
  zIndex: 2,
  boxShadow: '1px 0 0 #e5e7eb',
};

const timesheetStickyHeaderStyles: SxProps<Theme> = {
  ...timesheetStickyColumnStyles,
  backgroundColor: '#f9fafb',
  zIndex: 4,
};

export const sharedListStyles = {
  container: {
    width: '100%',
  },
  avatarCell: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
  },
  avatar: {
    width: 22,
    height: 22,
    backgroundColor: '#3b82f6',
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  cellText: {
    fontSize: '0.8125rem',
    color: '#6b7280',
  },
  cellChipBase: {
    fontSize: '0.875rem',
    fontWeight: 500,
    height: '20px',
  },
  roleChipsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 0.75,
  },
  paginationContainer: {
    mt: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 2,
    flexWrap: 'nowrap',
    px: 2,
    py: 2,
    width: '100%',
    boxSizing: 'border-box',
  },
  paginationText: {
    fontSize: '0.875rem',
    color: '#6b7280',
  },
  timesheetListTable: {
    loadingContainer: {
      p: 3,
      textAlign: 'center',
    } as SxProps<Theme>,
    wrapper: {
      width: '100%',
    } as SxProps<Theme>,
    paper: {
      width: '100%',
      overflow: 'hidden',
      boxShadow: 'none',
    } as SxProps<Theme>,
    tableContainer: (tableWidth: number): SxProps<Theme> => ({
      maxHeight: 'calc(100vh - 280px)',
      overflowX: 'auto',
      overflowY: 'auto',
      width: tableWidth > 0 ? `${tableWidth}px` : '100%',
      maxWidth: '100%',
      marginBottom: '0px',
      '&::-webkit-scrollbar': {
        height: '8px',
      },
      '&::-webkit-scrollbar-track': {
        backgroundColor: '#f1f1f1',
        borderRadius: '4px',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: '#c1c1c1',
        borderRadius: '4px',
        '&:hover': {
          backgroundColor: '#a8a8a8',
        },
      },
    }),
    table: {
      minWidth: '100%',
      tableLayout: 'auto',
    } as SxProps<Theme>,
    headerCell: (width: number, index: number, checkboxColumnWidth: number): SxProps<Theme> => ({
      ...timesheetBaseCellStyles,
      minWidth: width,
      fontWeight: 600,
      fontSize: '0.875rem',
      color: '#374151',
      textTransform: 'capitalize',
      backgroundColor: '#f9fafb',
      borderBottom: '2px solid #e5e7eb',
      ...(index === 0 ? { ...timesheetStickyHeaderStyles, left: 0 } : {}),
      ...(index === 1 ? { ...timesheetStickyHeaderStyles, left: checkboxColumnWidth } : {}),
    }),
    emptyStateCell: {
      py: 3,
    } as SxProps<Theme>,
    row: (rowBackground: string, isBlocked: boolean): SxProps<Theme> => ({
      backgroundColor: rowBackground,
      '& td': {
        backgroundColor: rowBackground,
      },
      '&:hover td': {
        backgroundColor: isBlocked ? '#dcfce7' : '#f8fafc',
      },
    }),
    stickyBodyCell: (left: number, rowBackground: string): SxProps<Theme> => ({
      ...timesheetBaseCellStyles,
      ...timesheetStickyColumnStyles,
      left,
      zIndex: 3,
      backgroundColor: rowBackground,
    }),
    bodyCell: timesheetBaseCellStyles,
    actionBar: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: 2,
      px: 2,
      py: 2,
      borderTop: '1px solid #e5e7eb',
      backgroundColor: '#fafafa',
      flexWrap: 'wrap',
    } as SxProps<Theme>,
    actionCountText: {
      alignSelf: 'center',
      fontSize: '0.875rem',
      color: '#6b7280',
      fontWeight: 500,
    } as SxProps<Theme>,
    actionButtonsContainer: {
      display: 'flex',
      gap: 1,
      flexWrap: 'wrap',
    } as SxProps<Theme>,
    actionButtonBase: {
      borderRadius: 0.5,
      textTransform: 'none',
      fontWeight: 600,
      minHeight: '22px !important',
      minWidth: 'auto !important',
      padding: '4px 12px !important',
      height: '30px !important',
    } as SxProps<Theme>,
    exportButton: {
      backgroundColor: '#6366f1',
      boxShadow: '0 1px 3px 0 rgba(99, 102, 241, 0.3)',
      '&:hover': {
        backgroundColor: '#4f46e5',
        boxShadow: '0 4px 6px -1px rgba(99, 102, 241, 0.3)',
      },
    } as SxProps<Theme>,
    printButton: {
      backgroundColor: '#6366f1',
      boxShadow: '0 1px 3px 0 rgba(99, 102, 241, 0.3)',
      '&:hover': {
        backgroundColor: '#4f46e5',
        boxShadow: '0 4px 6px -1px rgba(99, 102, 241, 0.3)',
      },
    } as SxProps<Theme>,
    exportRevenueButton: {
      backgroundColor: '#f97316',
      boxShadow: '0 1px 3px 0 rgba(249, 115, 22, 0.3)',
      '&:hover': {
        backgroundColor: '#ea580c',
        boxShadow: '0 4px 6px -1px rgba(249, 115, 22, 0.3)',
      },
    } as SxProps<Theme>,
    printRevenueButton: {
      backgroundColor: '#f97316',
      boxShadow: '0 1px 3px 0 rgba(249, 115, 22, 0.3)',
      '&:hover': {
        backgroundColor: '#ea580c',
        boxShadow: '0 4px 6px -1px rgba(249, 115, 22, 0.3)',
      },
    } as SxProps<Theme>,
    deleteButton: {
      boxShadow: '0 1px 3px 0 rgba(16, 185, 129, 0.3)',
    } as SxProps<Theme>,
    greenActionButton: {
      backgroundColor: '#10b981',
      boxShadow: '0 1px 3px 0 rgba(16, 185, 129, 0.3)',
      '&:hover': {
        backgroundColor: '#059669',
        boxShadow: '0 4px 6px -1px rgba(16, 185, 129, 0.3)',
      },
    } as SxProps<Theme>,
    button: (variant: 'export' | 'print' | 'exportRevenue' | 'printRevenue' | 'delete' | 'green'): SxProps<Theme> => {
      const base = {
        ...(sharedListStyles.timesheetListTable.actionButtonBase as object),
      };

      if (variant === 'export') {
        return { ...base, ...(sharedListStyles.timesheetListTable.exportButton as object) };
      }

      if (variant === 'print') {
        return { ...base, ...(sharedListStyles.timesheetListTable.printButton as object) };
      }

      if (variant === 'exportRevenue') {
        return { ...base, ...(sharedListStyles.timesheetListTable.exportRevenueButton as object) };
      }

      if (variant === 'printRevenue') {
        return { ...base, ...(sharedListStyles.timesheetListTable.printRevenueButton as object) };
      }

      if (variant === 'delete') {
        return { ...base, ...(sharedListStyles.timesheetListTable.deleteButton as object) };
      }

      return { ...base, ...(sharedListStyles.timesheetListTable.greenActionButton as object) };
    },
  },
  mainListOrder: {
    statusCodeText: (statusColor: string): SxProps<Theme> => ({
      color: statusColor,
      fontSize: '0.875rem',
      fontFamily: 'monospace',
      fontWeight: 500,
    }),
    statusNameText: (statusColor: string): SxProps<Theme> => ({
      color: statusColor,
      fontSize: '0.875rem',
      fontWeight: 500,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      maxWidth: '100%',
    }),
    bodyText: {
      color: '#374151',
      fontSize: '0.875rem',
    } as SxProps<Theme>,
    ellipsisBodyText: {
      color: '#374151',
      fontSize: '0.875rem',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      maxWidth: '100%',
    } as SxProps<Theme>,
    mutedBodyText: {
      color: '#6b7280',
      fontSize: '0.875rem',
    } as SxProps<Theme>,
    statusChip: (statusColor: string): SxProps<Theme> => ({
      backgroundColor: `${statusColor}20`,
      color: statusColor,
      fontSize: '0.75rem',
      height: '20px',
    }),
    amountText: {
      fontSize: '0.875rem',
      color: '#374151',
    } as SxProps<Theme>,
    amountMutedText: {
      fontSize: '0.875rem',
      color: '#6b7280',
    } as SxProps<Theme>,
    paginationContainer: {
      display: 'flex',
      alignItems: { xs: 'flex-start', md: 'center' },
      justifyContent: 'space-between',
      flexDirection: { xs: 'column', md: 'row' },
      gap: 2,
      px: 2,
      py: 2,
    } as SxProps<Theme>,
    paginationText: {
      color: '#6b7280',
    } as SxProps<Theme>,
  },
  timekeepingDetailTable: {
    tableContainer: (tableWidth: number): SxProps<Theme> => ({
      overflowX: 'auto',
      overflowY: 'hidden',
      width: tableWidth > 0 ? `${tableWidth}px` : '100%',
      maxWidth: '100%',
      boxSizing: 'border-box',
    }),
    checkboxHeaderCell: {
      width: '50px',
      backgroundColor: '#f9fafb',
    } as SxProps<Theme>,
    checkboxInput: {
      width: '20px',
      height: '20px',
      '& .MuiSvgIcon-root': {
        width: '20px',
        height: '20px',
      },
    } as SxProps<Theme>,
    headerCell: (align?: string): SxProps<Theme> => ({
      textAlign: (align || 'left') as any,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      fontWeight: 600,
      fontSize: '0.875rem',
      color: '#374151',
      backgroundColor: '#f9fafb',
      borderBottom: '2px solid #e5e7eb',
    }),
    groupHeaderRow: {
      backgroundColor: '#f3f4f6',
    } as SxProps<Theme>,
    groupHeaderCell: {
      fontWeight: 400,
      fontSize: '0.875rem',
      color: '#111827',
      borderBottom: '2px solid #e5e7eb',
      py: 1,
      backgroundColor: '#f3f4f6',
    } as SxProps<Theme>,
    checkboxBodyCell: {
      width: '50px',
      backgroundColor: 'white',
    } as SxProps<Theme>,
    bodyCell: (align?: string): SxProps<Theme> => ({
      textAlign: (align || 'left') as any,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      fontSize: '0.875rem',
      borderBottom: '1px solid #f3f4f6',
    }),
    row: {
      cursor: 'pointer',
      backgroundColor: 'white',
      '&:hover': {
        backgroundColor: '#f8fafc',
      },
      '&:last-child td': {
        borderBottom: 0,
      },
    } as SxProps<Theme>,
    attendanceToggleButton: (active: boolean, color: string, hoverColor: string): SxProps<Theme> => ({
      p: 0.5,
      width: 28,
      height: 28,
      minWidth: 28,
      minHeight: 28,
      color: active ? color : '#9ca3af',
      borderRadius: '4px',
      flexShrink: 0,
      transition: 'all 0.2s ease',
      '&:hover': {
        color: hoverColor,
      },
    }),
    booleanIconTrue: {
      color: '#22c55e',
      fontSize: 18,
    } as SxProps<Theme>,
    booleanIconFalse: {
      color: '#9ca3af',
      fontSize: 18,
    } as SxProps<Theme>,
    siteAllowanceButton: {
      position: 'absolute',
      left: -28,
      top: '10px',
      transform: 'translateY(-50%)',
      padding: '2px',
      color: '#f59e0b',
      backgroundColor: 'transparent',
      width: 30,
      height: 30,
      minWidth: 20,
      minHeight: 20,
      zIndex: 10,
      '&:hover': {
        backgroundColor: 'transparent',
        transform: 'translateY(-50%) scale(1.15)',
      },
      transition: 'all 0.2s ease',
    } as SxProps<Theme>,
    siteAllowanceIcon: {
      fontSize: '24px',
      fontWeight: 600,
    } as SxProps<Theme>,
    employeeNameText: (isSelected: boolean, isAbsent: boolean, baseCellText: object): SxProps<Theme> => ({
      ...(baseCellText as object),
      color: isSelected
        ? '#1d4ed8 !important'
        : isAbsent
          ? '#ef4444'
          : (baseCellText as any)?.color,
      fontWeight: isSelected ? 600 : (baseCellText as any)?.fontWeight,
      backgroundColor: isSelected ? '#dbeafe' : 'transparent',
      borderRadius: isSelected ? '6px' : 0,
      px: isSelected ? 0.75 : 0,
      py: isSelected ? 0.25 : 0,
      display: 'inline-block',
    }),
    dateText: (isSunday: boolean): SxProps<Theme> => ({
      fontSize: '0.875rem',
      color: isSunday ? '#ec4899' : '#111827',
      fontWeight: isSunday ? 600 : 400,
    }),
    workdayValueText: {
      fontSize: '0.875rem',
      fontWeight: 600,
      textAlign: 'center',
      width: '100%',
      color: '#111827',
    } as SxProps<Theme>,
    numericCellText: {
      textAlign: 'right',
      fontSize: '0.875rem',
      color: '#111827',
    } as SxProps<Theme>,
    inactiveAttendanceText: {
      fontSize: '0.75rem',
      color: '#9ca3af',
    } as SxProps<Theme>,
    notesText: {
      fontSize: '0.8rem',
      color: '#4b5563',
    } as SxProps<Theme>,
    emptyStateContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
    } as SxProps<Theme>,
    emptyStateText: {
      fontSize: '0.9rem',
      color: '#6b7280',
    } as SxProps<Theme>,
    paper: {
      width: '100%',
      overflow: 'hidden',
    } as SxProps<Theme>,
    table: {
      minWidth: '100%',
    } as SxProps<Theme>,
    avatarCellWithIcon: {
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      position: 'relative',
    } as SxProps<Theme>,
    outerWrapper: {
      flex: 1,
      minHeight: 0,
      mt: 0,
      position: 'relative',
    } as SxProps<Theme>,
    contentColumn: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      minHeight: 0,
    } as SxProps<Theme>,
    contentFlex: {
      flex: 1,
      minHeight: 0,
    } as SxProps<Theme>,
  },
};
